import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWebRTC } from '../context/WebRTCContext';
import './ActionAuditLog.css';

/**
 * ActionAuditLog - Tracks every command sent from frontend to desktop
 *
 * Two parts:
 * 1. Brief confirmation flash on trigger (used by QuickPunishment)
 * 2. Slide-in audit log panel accessible from header
 *
 * Intercepts featureManager.sendMessage calls to log actions.
 */

const MAX_LOG_ENTRIES = 200;

// Render an action icon that may be either an icon-font class (e.g.
// 'iconsminds-flash-2', now used by QuickPunishment instead of emoji, which
// tofu'd on Windows) or a literal glyph/emoji. Icon-font classes become an <i>;
// anything else renders as text.
const isIconClass = (icon) => typeof icon === 'string' && /^(simple-icon-|iconsminds-)/.test(icon);
const ActionIcon = ({ icon }) => (isIconClass(icon) ? <i className={icon} /> : <>{icon}</>);

// Singleton action log store (shared across components)
let _actionLog = [];
let _listeners = new Set();

function addAction(entry) {
    _actionLog = [entry, ..._actionLog.slice(0, MAX_LOG_ENTRIES - 1)];
    _listeners.forEach(fn => fn(_actionLog));
}

function updateAction(id, updates) {
    _actionLog = _actionLog.map(a => a.id === id ? { ...a, ...updates } : a);
    _listeners.forEach(fn => fn(_actionLog));
}

function useActionLog() {
    const [log, setLog] = useState(_actionLog);
    useEffect(() => {
        _listeners.add(setLog);
        return () => _listeners.delete(setLog);
    }, []);
    return log;
}

// Export for QuickPunishment and other components to use
export function logAction(action) {
    const entry = {
        id: `action_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        timestamp: Date.now(),
        label: action.label || action.type || 'Unknown Action',
        icon: action.icon || '\u26A1',
        featureId: action.featureId || null,
        messageType: action.messageType || action.type || null,
        status: 'sent', // sent | confirmed | failed | queued
        ...action,
    };
    addAction(entry);
    return entry.id;
}

export function confirmAction(id) {
    updateAction(id, { status: 'confirmed', confirmedAt: Date.now() });
}

export function failAction(id, reason) {
    updateAction(id, { status: 'failed', failedAt: Date.now(), reason });
}

/**
 * True if the operator triggered an action against `featureId` within the last
 * `withinMs`. Used to drop the redundant timeline toast for an action the
 * operator just initiated themselves — they already saw the confirmation flash
 * (ActionConfirmationToast) and it's in this audit log. Actions the operator did
 * NOT initiate (e.g. AI- or schedule-driven) have no matching entry, so their
 * toast still fires.
 */
export function hasRecentActionForFeature(featureId, withinMs = 8000) {
    if (!featureId) return false;
    const cutoff = Date.now() - withinMs;
    return _actionLog.some(a => a.featureId === featureId && a.timestamp >= cutoff);
}

/**
 * ActionConfirmationToast - Brief flash overlay when an action is executed
 */
export const ActionConfirmationToast = () => {
    const [toast, setToast] = useState(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        const handler = (log) => {
            const latest = log[0];
            if (!latest || latest.timestamp < Date.now() - 2000) return;

            setToast(latest);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => setToast(null), 1800);
        };

        _listeners.add(handler);
        return () => {
            _listeners.delete(handler);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    if (!toast) return null;

    const statusWord = toast.status === 'sent' ? 'Sent' :
        toast.status === 'confirmed' ? 'Confirmed' :
        toast.status === 'failed' ? 'Failed' : 'Queued';

    return (
        // role="status" + aria-live so the action's outcome (Sent / Failed) is
        // announced — an operator action can shock a person or close a window.
        <div className={`action-toast action-toast-${toast.status}`} role="status" aria-live="polite">
            <span className="action-toast-icon" aria-hidden="true"><ActionIcon icon={toast.icon} /></span>
            <span className="action-toast-label">{toast.label}</span>
            <span className={`action-toast-status status-${toast.status}`}>
                {statusWord}
            </span>
        </div>
    );
};

/**
 * ActionAuditLog - Full audit log panel
 */
const ActionAuditLog = ({ isOpen, onClose }) => {
    const log = useActionLog();
    const [filter, setFilter] = useState('all'); // all | sent | confirmed | failed

    const filteredLog = filter === 'all' ? log : log.filter(a => a.status === filter);

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    const handleClear = useCallback(() => {
        _actionLog = [];
        _listeners.forEach(fn => fn(_actionLog));
    }, []);

    if (!isOpen) return null;

    const statusCounts = {
        all: log.length,
        sent: log.filter(a => a.status === 'sent').length,
        confirmed: log.filter(a => a.status === 'confirmed').length,
        failed: log.filter(a => a.status === 'failed').length,
    };

    return (
        <>
            <div className="audit-backdrop" onClick={onClose} />
            <div className="audit-panel" role="dialog" aria-modal="true" aria-labelledby="audit-panel-title">
                <div className="audit-header">
                    <div className="audit-header-left">
                        <span className="audit-header-icon" aria-hidden="true">{'\u{1F4CB}'}</span>
                        <div>
                            <div className="audit-header-title" id="audit-panel-title">Action Audit Log</div>
                            <div className="audit-header-meta">
                                {log.length} action{log.length !== 1 ? 's' : ''} recorded
                            </div>
                        </div>
                    </div>
                    <button className="audit-close-btn" onClick={onClose}>&times;</button>
                </div>

                {/* Filter bar */}
                <div className="audit-filters">
                    {['all', 'sent', 'confirmed', 'failed'].map(f => (
                        <button
                            key={f}
                            className={`audit-filter ${filter === f ? 'active' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                            {statusCounts[f] > 0 && (
                                <span className="audit-filter-count">{statusCounts[f]}</span>
                            )}
                        </button>
                    ))}
                    <button className="audit-clear-btn" onClick={handleClear}>Clear</button>
                </div>

                {/* Log entries */}
                <div className="audit-content">
                    {filteredLog.length === 0 ? (
                        <div className="audit-empty">
                            <div className="audit-empty-icon">{'\u{1F4CB}'}</div>
                            <div className="audit-empty-text">No actions recorded</div>
                        </div>
                    ) : (
                        filteredLog.map(entry => (
                            <div key={entry.id} className={`audit-entry status-${entry.status}`}>
                                <div className="audit-entry-icon"><ActionIcon icon={entry.icon} /></div>
                                <div className="audit-entry-body">
                                    <div className="audit-entry-label">{entry.label}</div>
                                    <div className="audit-entry-details">
                                        {entry.featureId && (
                                            <span className="audit-entry-feature">{entry.featureId}</span>
                                        )}
                                        {entry.messageType && (
                                            <span className="audit-entry-type">{entry.messageType}</span>
                                        )}
                                        {entry.reason && (
                                            <span className="audit-entry-reason">{entry.reason}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="audit-entry-right">
                                    <div className={`audit-entry-status status-${entry.status}`}>
                                        {entry.status === 'sent' ? '\u2022' :
                                         entry.status === 'confirmed' ? '\u2713' :
                                         entry.status === 'failed' ? '\u2717' : '\u23F3'}
                                    </div>
                                    <div className="audit-entry-time">
                                        {formatAuditTime(entry.timestamp)}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

function formatAuditTime(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default ActionAuditLog;
